/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { SearchApi } from '@alfresco/js-api';
import { from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { UserPreferenceService } from '../shared/user-preference.service';

/** Preference key holding the user's saved searches, as a JSON array. */
const SAVED_PREFERENCE = 'aca.search.saved';

/** Every field the advanced form can constrain. Empty means "not applied". */
export interface SearchCriteria {
  term: string;
  documentType: string;
  owner: string;
  department: string;
  tag: string;
  status: string;
  contentText: string;
  createdFrom: string;
  createdTo: string;
  modifiedFrom: string;
  modifiedTo: string;
  /** Minimum content size in bytes; 0 means no lower bound. */
  sizeMin: number;
  /** Maximum content size in bytes; 0 means no upper bound. */
  sizeMax: number;
}

export const EMPTY_CRITERIA: SearchCriteria = {
  term: '',
  documentType: '',
  owner: '',
  department: '',
  tag: '',
  status: '',
  contentText: '',
  createdFrom: '',
  createdTo: '',
  modifiedFrom: '',
  modifiedTo: '',
  sizeMin: 0,
  sizeMax: 0
};

export interface SavedSearch {
  name: string;
  criteria: SearchCriteria;
}

export interface SearchHit {
  id: string;
  name: string;
  title: string;
  isFolder: boolean;
  location: string;
  description: string;
  modifiedAt: string;
  modifiedByName: string;
  sizeInBytes?: number;
  documentType: string;
  status: string;
  documentCode: string;
  tags: string[];
  /**
   * Set when the words were found by reading the page rather than by anything
   * the repository holds — the page they were on, and the text around them.
   */
  ocrPage?: number;
  /** Every page the words were found on; ocrPage is the best of them. */
  ocrPages?: number[];
  ocrFragments?: string[];
}

export interface SearchResults {
  hits: SearchHit[];
  /** Exact number of matches, or `null` when the repository would not give one. */
  total: number | null;
  hasMoreItems: boolean;
}

export type SearchSort = 'cm:modified' | 'cm:created' | 'cm:name' | 'score';

/**
 * Advanced document search (UI-07).
 *
 * Builds one AFTS query from the form and lets the repository do the work; no
 * result is filtered or re-ranked in the browser.
 *
 * The same repository behaviours that shaped All Documents apply here:
 *
 * - `dms:` properties match only in exact form (`=dms:status:"approved"`);
 * - `totalItems` is not a true hit count, so an exact total is only reported
 *   when a page did not fill up;
 * - full-text matching runs against the search index, which trails content
 *   changes by a batch cycle (~15s).
 */
@Injectable({ providedIn: 'root' })
export class DocumentSearchService {
  private readonly api = inject(AlfrescoApiService);
  private readonly preferences = inject(UserPreferenceService);

  private get searchApi(): SearchApi {
    return new SearchApi(this.api.getInstance() as any);
  }

  /** Runs a search and returns one page of hits. */
  search(criteria: SearchCriteria, sort: SearchSort, skipCount: number, maxItems: number): Observable<SearchResults> {
    const body: any = {
      query: { query: this.buildQuery(criteria), language: 'afts' },
      paging: { skipCount, maxItems },
      include: ['properties', 'path'],
      highlight: { fields: [{ field: 'cm:content' }] }
    };

    if (sort !== 'score') {
      body.sort = [{ type: 'FIELD', field: sort, ascending: false }];
    }

    return from(this.searchApi.search(body) as Promise<any>).pipe(
      map((res) => {
        const pagination = res?.list?.pagination ?? {};
        const hasMoreItems = !!pagination.hasMoreItems;
        return {
          hits: (res?.list?.entries ?? []).map((e: any) => this.toHit(e.entry)),
          hasMoreItems,
          total: hasMoreItems ? null : skipCount + (pagination.count ?? 0)
        };
      })
    );
  }

  /** True when the form would produce a query with no user-supplied constraint. */
  isEmpty(criteria: SearchCriteria): boolean {
    return this.clauses(criteria).length === 0;
  }

  /** The AFTS query the current criteria produce, shown to the user for transparency. */
  preview(criteria: SearchCriteria): string {
    return this.buildQuery(criteria);
  }

  // ---- saved searches -----------------------------------------------------

  saved(): Observable<SavedSearch[]> {
    return this.preferences.readJson<SavedSearch[]>(SAVED_PREFERENCE, []);
  }

  /** Saves under `name`, replacing any existing entry with the same name. */
  save(name: string, criteria: SearchCriteria): Observable<SavedSearch[]> {
    return this.saved().pipe(
      switchMap((list) => {
        const next = [...list.filter((entry) => entry.name !== name), { name, criteria }];
        return this.preferences.writeJson(SAVED_PREFERENCE, next).pipe(map(() => next));
      })
    );
  }

  remove(name: string): Observable<SavedSearch[]> {
    return this.saved().pipe(
      switchMap((list) => {
        const next = list.filter((entry) => entry.name !== name);
        return this.preferences.writeJson(SAVED_PREFERENCE, next).pipe(map(() => next));
      })
    );
  }

  // ---- query building -----------------------------------------------------

  private buildQuery(criteria: SearchCriteria): string {
    const clauses = this.clauses(criteria);
    const scope = '(TYPE:"cm:content" OR TYPE:"cm:folder") AND NOT TYPE:"cm:dictionaryModel"';
    return clauses.length ? [scope, ...clauses].join(' AND ') : scope;
  }

  /** The constraints the user actually supplied, each already escaped. */
  private clauses(criteria: SearchCriteria): string[] {
    const clauses: string[] = [];
    const quote = (value: string) => '"' + value.replace(/["\\]/g, '\\$&') + '"';

    if (criteria.term.trim()) {
      const term = criteria.term.trim().replace(/["\\]/g, '\\$&');
      clauses.push(`(cm:name:"*${term}*" OR cm:title:${quote(term)} OR cm:description:${quote(term)} OR TEXT:${quote(term)})`);
    }
    if (criteria.contentText.trim()) {
      clauses.push(`TEXT:${quote(criteria.contentText.trim())}`);
    }

    // `dms:` properties only match in the exact-match form in this repository
    if (criteria.documentType) {
      clauses.push(`=dms:documentType:${quote(criteria.documentType)}`);
    }
    if (criteria.status) {
      clauses.push(`=dms:status:${quote(criteria.status)}`);
    }
    if (criteria.department.trim()) {
      clauses.push(`=dms:department:${quote(criteria.department.trim())}`);
    }
    if (criteria.owner.trim()) {
      clauses.push(`(=dms:documentOwner:${quote(criteria.owner.trim())} OR creator:${quote(criteria.owner.trim())})`);
    }
    if (criteria.tag.trim()) {
      clauses.push(`TAG:${quote(criteria.tag.trim())}`);
    }

    const range = (field: string, start: string, end: string) => {
      if (!start && !end) {
        return;
      }
      clauses.push(`${field}:[${start ? this.asDay(start) : 'MIN'} TO ${end ? this.asDay(end) : 'MAX'}]`);
    };
    range('cm:created', criteria.createdFrom, criteria.createdTo);
    range('cm:modified', criteria.modifiedFrom, criteria.modifiedTo);

    if (criteria.sizeMin || criteria.sizeMax) {
      const min = criteria.sizeMin || 0;
      const max = criteria.sizeMax || 'MAX';
      clauses.push(`cm:content.size:[${min} TO ${max}]`);
    }

    return clauses;
  }

  /** `yyyy-MM-dd` from a date input, which is what AFTS date ranges expect. */
  private asDay(value: string): string {
    return `"${value}"`;
  }

  private toHit(entry: any): SearchHit {
    const props = entry.properties ?? {};
    const tags: string[] = props['cm:taggable'] ?? [];

    return {
      id: entry.id,
      name: entry.name,
      title: props['cm:title'] ?? '',
      isFolder: !!entry.isFolder,
      location: this.locationOf(entry),
      description: props['cm:description'] ?? '',
      modifiedAt: entry.modifiedAt,
      modifiedByName: entry.modifiedByUser?.displayName ?? entry.modifiedByUser?.id ?? '',
      sizeInBytes: entry.content?.sizeInBytes,
      documentType: props['dms:documentType'] ?? '',
      status: props['dms:status'] ?? '',
      documentCode: props['dms:documentCode'] ?? '',
      tags: Array.isArray(tags) ? tags : []
    };
  }

  private locationOf(entry: any): string {
    const path: string = entry?.path?.name ?? '';
    if (!path) {
      return '';
    }
    return path.replace(/^\/Company Home\/?/, '').replace(/\//g, ' / ') || 'Company Home';
  }
}
